import React, { FC, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import API from "utils/helpers/APIHelper";
import { Helmet } from "react-helmet";
import CommunityGallery, { heroPreloadHref } from "components/CommunityGallery/CommunityGallery";

const COUNTRIES = [
  "Argentina", "Australia", "Austria", "Belgium", "Bolivia", "Brazil", "Canada", "Chile",
  "China", "Colombia", "Costa Rica", "Denmark", "Ecuador", "Finland", "France", "Germany",
  "Ireland", "Italy", "Japan", "Mexico", "Netherlands", "New Zealand", "Norway", "Peru",
  "Poland", "Portugal", "South Korea", "Spain", "Sweden", "Switzerland", "United Kingdom",
  "United States", "Uruguay", "Other",
];

const HOME_UNIVERSITIES = [
  "NEOMA Business School", "University of Groningen", "EDHEC Business School",
  "Maastricht University", "Sciences Po Lyon",
  "Institut Polytechnique des Sciences Avancées (IPSA)", "EMLYON Business School",
  "Pforzheim University", "University of Innsbruck", "Newcastle University", "IAE Angers",
  "Université Toulouse Capitole", "Hochschule Stralsund", "University of Exeter",
  "Erasmus University Rotterdam", "Other",
];

const HOST_UNIVERSITIES = [
  "PUCP", "Universidad del Pacífico", "UPC", "Universidad de Lima", "USIL", "UNMSM", "ESAN",
  "Other",
];

const NEIGHBOURHOODS = [
  "Miraflores", "Barranco", "San Isidro", "Surco", "Jesús María", "Magdalena", "San Miguel", "San Borja",
  "Other",
];

const INTERESTS: { value: string; label: string }[] = [
  { value: "parties", label: "🎉 Parties" },
  { value: "trips", label: "🌴 Trips around Peru" },
  { value: "surf", label: "🏄 Surf" },
  { value: "food", label: "🍖 Food & meet-ups" },
  { value: "sports", label: "⚽ Sports" },
  { value: "spanish", label: "🗣️ Spanish practice" },
];

const validationSchema = Yup.object({
  full_name: Yup.string().trim().required("Tell us your name"),
  email: Yup.string().trim().email("Check this email address").required("We need an email to confirm your entry"),
  whatsapp_number: Yup.string()
    .trim()
    .matches(/^\+?[0-9\s()-]{8,20}$/, "Include your country code, e.g. +49 170 1234567")
    .required("WhatsApp is how the community runs"),
  nationality: Yup.string().trim().required("Tell us where you're from"),
  home_university: Yup.string().trim().required("Tell us your home university"),
  host_university: Yup.string().trim().required("Tell us your university in Lima"),
  neighbourhood: Yup.string().trim().required("Tell us where you're staying"),
  referred_by: Yup.string().trim().required("Tell us who referred you"),
  // One checkbox covers both the WhatsApp group and email updates.
  consent_whatsapp: Yup.boolean().oneOf([true], "You need to accept this to join the community"),
});

const PageCommunity: FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [groupUrl, setGroupUrl] = useState<string | null>(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // "Select" fields where choosing "Other" reveals a free-text box.
  // The select's own value tracks which UI mode we're in; the typed
  // text becomes the actual value stored on the formik field.
  const [nationalityMode, setNationalityMode] = useState<"select" | "other">("select");
  const [homeUniMode, setHomeUniMode] = useState<"select" | "other">("select");
  const [hostUniMode, setHostUniMode] = useState<"select" | "other">("select");
  const [neighbourhoodMode, setNeighbourhoodMode] = useState<"select" | "other">("select");

  const utm = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || p.get("source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      referrer_url: document.referrer || "",
    };
  }, []);

  const formRef = React.useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (submitted) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [submitted]);

  // Let the hero (giveaway hook, benefits) land first, then guide the
  // visitor straight to the form so they don't have to scroll manually.
  useEffect(() => {
    if (submitted) return;
    const timer = setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      full_name: "",
      email: "",
      whatsapp_number: "",
      instagram_handle: "",
      nationality: "",
      home_university: "",
      host_university: "",
      neighbourhood: "",
      referred_by: "",
      arrival_date: "",
      departure_date: "",
      interests: [] as string[],
      consent_whatsapp: false,
      website: "", // honeypot — must stay empty
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setServerError(null);
      try {
        const data: any = await API.post("/api/v1/community_signups", {
          community_signup: {
            ...values,
            consent_marketing: values.consent_whatsapp, // one checkbox covers both
            ...utm,
          },
          website: values.website,
        });
        setGroupUrl(data?.whatsapp_group_url || null);
        setAlreadyRegistered(Boolean(data?.already_registered));
        setSubmitted(true);
      } catch (error: any) {
        setServerError(
          error?.response?.data?.message ||
            "That didn't go through. Check your connection and try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fieldError = (name: keyof typeof formik.values) =>
    formik.touched[name] && formik.errors[name] ? (
      <p className="mt-1.5 text-sm text-red-500 dark:text-red-400">{formik.errors[name] as string}</p>
    ) : null;

  const inputClass =
    "w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 " +
    "px-4 py-3 text-neutral-900 dark:text-neutral-50 placeholder-neutral-400 dark:placeholder-neutral-500 " +
    "focus:border-primary-6000 focus:outline-none focus:ring-2 focus:ring-primary-6000/30 transition";

  const labelClass = "mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-300";

  const toggleInterest = (value: string) => {
    const next = formik.values.interests.includes(value)
      ? formik.values.interests.filter((i) => i !== value)
      : [...formik.values.interests, value];
    formik.setFieldValue("interests", next);
  };

  // --- select-with-other handlers -----------------------------------
  const handleNationalityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "Other") {
      setNationalityMode("other");
      formik.setFieldValue("nationality", "");
    } else {
      setNationalityMode("select");
      formik.setFieldValue("nationality", val);
    }
  };

  const handleHomeUniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "Other") {
      setHomeUniMode("other");
      formik.setFieldValue("home_university", "");
    } else {
      setHomeUniMode("select");
      formik.setFieldValue("home_university", val);
    }
  };

  const handleHostUniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "Other") {
      setHostUniMode("other");
      formik.setFieldValue("host_university", "");
    } else {
      setHostUniMode("select");
      formik.setFieldValue("host_university", val);
    }
  };

  const handleNeighbourhoodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "Other") {
      setNeighbourhoodMode("other");
      formik.setFieldValue("neighbourhood", "");
    } else {
      setNeighbourhoodMode("select");
      formik.setFieldValue("neighbourhood", val);
    }
  };

  // ---------------------------------------------------------------- success
  if (submitted) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-900 px-5 py-16 text-neutral-900 dark:text-neutral-50">
        <Helmet>
          <title>You're in — Nomadroof Community</title>
        </Helmet>
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-6000 text-3xl">
            🎉
          </div>
          <h1 className="text-3xl font-bold leading-tight">
            {alreadyRegistered ? "You're already on the list" : "You're in."}
          </h1>
          <p className="mt-3 text-neutral-600 dark:text-neutral-400">
            {alreadyRegistered
              ? "No need to register twice — your giveaway entry is safe."
              : "Your name is in the draw for a free surf lesson. Now join the group so you don't miss the next trip."}
          </p>

          {groupUrl && (
            <a
              href={groupUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 block w-full rounded-xl bg-primary-6000 px-6 py-4 text-lg font-bold text-neutral-50 transition hover:bg-primary-700"
            >
              Join the WhatsApp group
            </a>
          )}

          <p className="mt-6 text-sm text-neutral-500">
            Follow us at Instagram —{" "}
            <a href="https://instagram.com/nomadroof" className="text-primary-6000 underline">
              @nomadroof
            </a>
          </p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------- form
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50">
      <Helmet>
        <title>Join the Nomadroof Community — Lima exchange students</title>
        <meta
          name="description"
          content="Trips, parties and weekly meet-ups with exchange students in Lima. Register and enter the draw for a free surf lesson."
        />
        <meta property="og:title" content="Your exchange semester starts here" />
        <meta
          property="og:description"
          content="Trips around Peru, free guest list, weekly meet-ups. Register now and enter our giveaway for a free surf lesson."
        />
        <meta property="og:type" content="website" />
        {/* The first gallery tile is the likely LCP element */}
        <link rel="preload" as="image" href={heroPreloadHref} />
      </Helmet>

      {/* Hero */}
      <header className="px-5 pt-14 pb-8">
        <div className="mx-auto max-w-md">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-6000">
            Lima · Exchange semester
          </p>
          <h1 className="text-4xl font-bold leading-[1.1] text-neutral-900 dark:text-neutral-50">
            Your exchange semester starts here.
          </h1>
          <p className="mt-4 text-neutral-600 dark:text-neutral-400">
            Staying somewhere else in Lima? You can still join the Nomadroof Community — trips,
            events, parties and meet-ups with exchange students from around the world.
          </p>

          <ul className="mt-7 space-y-3">
            {[
              ["🎉", "Free guest list access to selected parties"],
              ["🌴", "Trips and adventures around Peru"],
              ["🍖", "Weekly meet-ups and social events"],
              ["🌎", "A community of exchange students from everywhere"],
            ].map(([icon, text]) => (
              <li key={text} className="flex items-start gap-3 text-neutral-800 dark:text-neutral-200">
                <span className="text-lg leading-6">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 rounded-xl border border-primary-6000/40 bg-primary-6000/10 px-4 py-3">
            <p className="text-sm font-semibold text-primary-6000">
              🏄 Register now and you're automatically entered in our giveaway for a free surf lesson.
            </p>
          </div>

          {/* Proof that the trips and parties are real */}
          <CommunityGallery />
        </div>
      </header>

      {/* Form */}
      <main className="px-5 pb-20">
        <form ref={formRef} onSubmit={formik.handleSubmit} className="mx-auto max-w-md space-y-5" noValidate>
          {/* honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={formik.values.website}
            onChange={formik.handleChange}
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />

          <div>
            <label htmlFor="full_name" className={labelClass}>Full name</label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              placeholder="Lena Fischer"
              className={inputClass}
              value={formik.values.full_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {fieldError("full_name")}
          </div>

          <div>
            <label htmlFor="email" className={labelClass}>Email</label>
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              placeholder="your-email@gmail.com"
              className={inputClass}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {fieldError("email")}
          </div>

          <div>
            <label htmlFor="whatsapp_number" className={labelClass}>WhatsApp number</label>
            <input
              id="whatsapp_number"
              name="whatsapp_number"
              type="tel"
              inputMode="tel"
              placeholder="+49 170 1234567"
              className={inputClass}
              value={formik.values.whatsapp_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <p className="mt-1.5 text-xs text-neutral-500">Include your country code.</p>
            {fieldError("whatsapp_number")}
          </div>

          {/* Nationality — required, select with Other → free text */}
          <div>
            <label htmlFor="nationality" className={labelClass}>Country</label>
            <select
              id="nationality"
              name="nationality"
              className={inputClass}
              value={nationalityMode === "other" ? "Other" : formik.values.nationality}
              onChange={handleNationalityChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select your Nationality…</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {nationalityMode === "other" && (
              <input
                type="text"
                name="nationality"
                placeholder="Type your country"
                className={`${inputClass} mt-2`}
                value={formik.values.nationality}
                onChange={(e) => formik.setFieldValue("nationality", e.target.value)}
                onBlur={formik.handleBlur}
              />
            )}
            {fieldError("nationality")}
          </div>

          {/* Home university — required, select with Other → free text */}
          <div>
            <label htmlFor="home_university" className={labelClass}>Home university</label>
            <select
              id="home_university"
              name="home_university"
              className={inputClass}
              value={homeUniMode === "other" ? "Other" : formik.values.home_university}
              onChange={handleHomeUniChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select…</option>
              {HOME_UNIVERSITIES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            {homeUniMode === "other" && (
              <input
                type="text"
                name="home_university"
                placeholder="Type your home university"
                className={`${inputClass} mt-2`}
                value={formik.values.home_university}
                onChange={(e) => formik.setFieldValue("home_university", e.target.value)}
                onBlur={formik.handleBlur}
              />
            )}
            {fieldError("home_university")}
          </div>

          {/* University in Lima — required, select with Other → free text */}
          <div>
            <label htmlFor="host_university" className={labelClass}>University in Lima</label>
            <select
              id="host_university"
              name="host_university"
              className={inputClass}
              value={hostUniMode === "other" ? "Other" : formik.values.host_university}
              onChange={handleHostUniChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select…</option>
              {HOST_UNIVERSITIES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            {hostUniMode === "other" && (
              <input
                type="text"
                name="host_university"
                placeholder="Type your university in Lima"
                className={`${inputClass} mt-2`}
                value={formik.values.host_university}
                onChange={(e) => formik.setFieldValue("host_university", e.target.value)}
                onBlur={formik.handleBlur}
              />
            )}
            {fieldError("host_university")}
          </div>

          {/* Neighbourhood — required, select with Other → free text */}
          <div>
            <label htmlFor="neighbourhood" className={labelClass}>Where are you staying?</label>
            <select
              id="neighbourhood"
              name="neighbourhood"
              className={inputClass}
              value={neighbourhoodMode === "other" ? "Other" : formik.values.neighbourhood}
              onChange={handleNeighbourhoodChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select…</option>
              {NEIGHBOURHOODS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {neighbourhoodMode === "other" && (
              <input
                type="text"
                name="neighbourhood"
                placeholder="Type your neighbourhood"
                className={`${inputClass} mt-2`}
                value={formik.values.neighbourhood}
                onChange={(e) => formik.setFieldValue("neighbourhood", e.target.value)}
                onBlur={formik.handleBlur}
              />
            )}
            {fieldError("neighbourhood")}
          </div>

          {/* Referred by — required, free text */}
          <div>
            <label htmlFor="referred_by" className={labelClass}>Who referred you?</label>
            <input
              id="referred_by"
              name="referred_by"
              type="text"
              placeholder="Type your friend's full name"
              className={inputClass}
              value={formik.values.referred_by}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              No one referred you? No problem — just type "None".
            </p>
            {fieldError("referred_by")}
          </div>

          {/* Optional block */}
          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 p-5">
            <p className="mb-4 text-sm font-semibold text-neutral-600 dark:text-neutral-400">
              Optional — helps us plan trips you'll actually want
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="instagram_handle" className={labelClass}>Instagram</label>
                <input
                  id="instagram_handle"
                  name="instagram_handle"
                  type="text"
                  placeholder="@yourhandle"
                  className={inputClass}
                  value={formik.values.instagram_handle}
                  onChange={formik.handleChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="arrival_date" className={labelClass}>Arrival</label>
                  <input
                    id="arrival_date"
                    name="arrival_date"
                    type="date"
                    className={inputClass}
                    value={formik.values.arrival_date}
                    onChange={formik.handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="departure_date" className={labelClass}>Leaving</label>
                  <input
                    id="departure_date"
                    name="departure_date"
                    type="date"
                    className={inputClass}
                    value={formik.values.departure_date}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>

              <div>
                <span className={labelClass}>What are you into?</span>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => {
                    const active = formik.values.interests.includes(i.value);
                    return (
                      <button
                        key={i.value}
                        type="button"
                        onClick={() => toggleInterest(i.value)}
                        aria-pressed={active}
                        className={`rounded-full border px-3.5 py-2 text-sm transition ${
                          active
                            ? "border-primary-6000 bg-primary-6000 font-semibold text-neutral-50"
                            : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-400"
                        }`}
                      >
                        {i.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Consent — required to join. Covers the WhatsApp group AND email updates. */}
          <label className="flex cursor-pointer items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              name="consent_whatsapp"
              checked={formik.values.consent_whatsapp}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="mt-1 h-5 w-5 accent-primary-6000"
            />
            <span>
              Add me to the Nomadroof Community and send me event reminders by email.
            </span>
          </label>
          {fieldError("consent_whatsapp")}

          {serverError && (
            <div className="rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
              {serverError}
            </div>
          )}

          <button
            type="submit"
            disabled={formik.isSubmitting || !formik.values.consent_whatsapp}
            className="w-full rounded-xl bg-primary-6000 px-6 py-4 text-lg font-bold text-neutral-50 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {formik.isSubmitting ? "Registering…" : "Register and enter the giveaway"}
          </button>

          {!formik.values.consent_whatsapp && (
            <p className="text-center text-xs text-neutral-500">
              Tick the box above to join the community.
            </p>
          )}

          <p className="text-center text-xs text-neutral-500">
            We use your details to run the community and send you updates. Leave the group or
            unsubscribe any time —{" "}
            <a href="/policy" className="underline">privacy policy</a>.
          </p>
        </form>
      </main>
    </div>
  );
};

export default PageCommunity;